import {
  Box,
  Collapse,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDown, ChevronRight, History } from "lucide-react";
import { memo } from "react";
import { format } from "date-fns";
import { Avatar } from "components/ds";
import type { MFReviewAccount } from "state/members/accessReviews";
import { ReviewChip } from "../Table/AccountRow";
import { reviewAccountDisplayName } from "../reviewLogic";

/**
 * PreviouslyReviewedSection — the stage-1 rows a later review stage subsumed
 * (PLO-4806). When the same person reviews stage 1 and stage 2, the stage-2 row
 * is the actionable one; its decided stage-1 predecessor is folded out of the
 * main list and shown here, read-only, so "what was decided last stage" stays
 * reachable without duplicating the person as an actionable row.
 *
 * Renders nothing when there are no folded predecessors — a single-stage review
 * (and any stage-2 reviewer who didn't also review stage 1) is untouched.
 */
const PreviouslyReviewedSection = memo(
  ({
    accounts,
    outcomeOptions,
  }: {
    accounts: MFReviewAccount[];
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
  }) => {
    const { isOpen, onToggle } = useDisclosure();

    if (!accounts.length) return null;

    return (
      <Box
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="component.xl"
        bg="bg.section"
        overflow="hidden"
      >
        <Flex
          as="button"
          type="button"
          onClick={onToggle}
          align="center"
          justify="space-between"
          w="full"
          px={4}
          py={3}
          _hover={{ bg: "neutral.75" }}
          transition="background-color 120ms"
          aria-expanded={isOpen}
        >
          <HStack spacing="sm">
            <Icon as={History} boxSize={4} color="text.body.tertiary" />
            <Text fontSize="sm" fontWeight={600} color="text.body.secondary">
              Previously reviewed ({accounts.length})
            </Text>
            <Text fontSize="xs" color="text.body.tertiary">
              decided at an earlier stage, not part of this re-review
            </Text>
          </HStack>
          <Icon
            as={isOpen ? ChevronDown : ChevronRight}
            boxSize={4}
            color="text.body.tertiary"
          />
        </Flex>

        <Collapse in={isOpen} animateOpacity>
          <VStack align="stretch" spacing={0} px={4} pb={2}>
            {accounts.map((account) => (
              <PreviouslyReviewedRow
                key={account.id}
                account={account}
                outcomeOptions={outcomeOptions}
              />
            ))}
          </VStack>
        </Collapse>
      </Box>
    );
  },
);

const PreviouslyReviewedRow = memo(
  ({
    account,
    outcomeOptions,
  }: {
    account: MFReviewAccount;
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
  }) => {
    const name = reviewAccountDisplayName(account);
    const reviewedAt = account.reviewedAt
      ? safeFormat(account.reviewedAt)
      : null;

    return (
      <HStack
        spacing="sm"
        py={2}
        borderTop="1px solid"
        borderColor="border.subtle"
        _first={{ borderTop: "none" }}
      >
        <Avatar
          name={name}
          src={account.member?.profilePictureUrl || undefined}
          size="xs"
        />
        <VStack align="flex-start" spacing="2xs" flex="1" minW={0}>
          <Text
            fontSize="sm"
            fontWeight={500}
            color="text.body.primary"
            noOfLines={1}
          >
            {name}
          </Text>
          {account.member?.email && (
            <Text fontSize="xs" color="text.body.tertiary" noOfLines={1}>
              {account.member.email}
            </Text>
          )}
        </VStack>
        {reviewedAt && (
          <Text fontSize="xs" color="text.body.tertiary" whiteSpace="nowrap">
            {reviewedAt}
          </Text>
        )}
        <ReviewChip status={account.review} outcomeOptions={outcomeOptions} />
      </HStack>
    );
  },
);

const safeFormat = (iso: string): string | null => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : format(d, "MMM d, yyyy");
};

export default PreviouslyReviewedSection;
